import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";
import { Trash2, Pencil, Plus } from "lucide-react";
import { ImageUploader } from "@/components/admin/ImageUploader";

const EMPTY = {
  title_fr: "", title_pt: "", description_fr: "", description_pt: "",
  audio_url: "", cover_image: "", duration: "", published: true,
};

export const PodcastManager = () => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const { data: episodes = [] } = useQuery({ queryKey: ["admin-podcast"], queryFn: async () => (await api.get("/admin/podcast")).data });

  const save = useMutation({
    mutationFn: async () => (editing ? api.put(`/admin/podcast/${editing.id}`, form) : api.post("/admin/podcast", form)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-podcast"] });
      qc.invalidateQueries({ queryKey: ["podcast"] });
      toast("Episódio salvo!");
      setOpen(false);
      setForm(EMPTY);
      setEditing(null);
    },
    onError: (e) => toast(formatApiError(e.response?.data?.detail)),
  });

  const remove = useMutation({
    mutationFn: async (id) => api.delete(`/admin/podcast/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-podcast"] });
      qc.invalidateQueries({ queryKey: ["podcast"] });
      toast("Episódio removido.");
    },
  });

  const openEdit = (ep) => { setEditing(ep); setForm(ep); setOpen(true); };
  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };

  return (
    <div className="mt-6" data-testid="podcast-manager">
      <div className="flex justify-end mb-4">
        <Button onClick={openNew} data-testid="podcast-new-button"><Plus className="w-4 h-4 mr-1" /> Novo episódio</Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow><TableHead>Título (PT)</TableHead><TableHead>Duração</TableHead><TableHead>Publicado</TableHead><TableHead /></TableRow>
        </TableHeader>
        <TableBody>
          {episodes.map((ep) => (
            <TableRow key={ep.id} data-testid={`podcast-row-${ep.id}`}>
              <TableCell>{ep.title_pt}</TableCell>
              <TableCell>{ep.duration}</TableCell>
              <TableCell>{ep.published ? "Sim" : "Não"}</TableCell>
              <TableCell className="flex gap-2 justify-end">
                <Button size="icon" variant="ghost" onClick={() => openEdit(ep)} data-testid={`podcast-edit-${ep.id}`}><Pencil className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => remove.mutate(ep.id)} data-testid={`podcast-delete-${ep.id}`}><Trash2 className="w-4 h-4" /></Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="podcast-form-dialog">
          <DialogHeader><DialogTitle>{editing ? "Editar episódio" : "Novo episódio"}</DialogTitle><DialogDescription>Preencha os campos em português e francês.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Título (PT)</Label><Input data-testid="podcast-input-title_pt" value={form.title_pt} onChange={(e) => setForm({ ...form, title_pt: e.target.value })} /></div>
            <div><Label>Título (FR)</Label><Input value={form.title_fr} onChange={(e) => setForm({ ...form, title_fr: e.target.value })} /></div>
            <div><Label>Descrição (PT)</Label><Textarea value={form.description_pt} onChange={(e) => setForm({ ...form, description_pt: e.target.value })} /></div>
            <div><Label>Descrição (FR)</Label><Textarea value={form.description_fr} onChange={(e) => setForm({ ...form, description_fr: e.target.value })} /></div>
            <div><Label>Áudio (URL / RSS)</Label><Input value={form.audio_url} onChange={(e) => setForm({ ...form, audio_url: e.target.value })} /></div>
            <div className="col-span-2">
              <Label>Imagem de capa</Label>
              <ImageUploader value={form.cover_image} onChange={(url) => setForm({ ...form, cover_image: url })} testId="podcast-cover" />
            </div>
            <div><Label>Duração</Label><Input value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} /></div>
          </div>
          <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="podcast-save-button" className="mt-4">Salvar</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};
